/**
 * Committee utility functions
 *
 * Helper functions for working with committee member data, including
 * building committee member displays and lookups.
 */

import type { CommitteeMember, MemberSummary } from '../types/api.types';

/**
 * Display representation of a committee member
 */
export interface CommitteeMemberDisplay {
  id: string;
  role: string;
  name: string;
}

/**
 * Member lookup function type
 * Maps member ID to member name
 */
export type MemberLookupFn = (memberId: string) => string;

/**
 * Build a list of committee members with their roles and names
 *
 * @param committee - Committee member data with role assignments
 * @param memberLookup - Function to resolve member ID to name
 * @returns Array of committee members with their display information
 *
 * @example
 * ```typescript
 * const memberLookup = (id: string) => {
 *   const member = memberDict[id];
 *   return member ? formatMemberName(member) : id;
 * };
 * const members = buildCommitteeMembers(committee, memberLookup);
 * ```
 */
export const buildCommitteeMembers = (
  committee: CommitteeMember,
  memberLookup: MemberLookupFn
): CommitteeMemberDisplay[] => {
  const members: CommitteeMemberDisplay[] = [];

  const roles: Array<{ id: string | undefined; role: string }> = [
    { id: committee.chairman, role: 'Chairman' },
    { id: committee.captain, role: 'Captain' },
    { id: committee.secretary, role: 'Secretary' },
    { id: committee.treasurer, role: 'Treasurer' },
    { id: committee.committeeMember1, role: 'Committee Member' },
    { id: committee.committeeMember2, role: 'Committee Member' },
  ];

  roles.forEach(({ id, role }) => {
    if (id) {
      members.push({
        id,
        role,
        name: memberLookup(id),
      });
    }
  });

  return members;
};

/**
 * Create a member lookup function from an array of member summaries
 * Uses O(n) array search - for better performance, use entity selectors
 *
 * @param memberSummaries - Array of member summaries
 * @returns Function that looks up member name by ID
 *
 * @deprecated Prefer using entity selectors with formatMemberName for O(1) lookups
 */
export const createMemberLookupFromArray = (memberSummaries: MemberSummary[]): MemberLookupFn => {
  return (memberId: string): string => {
    const member = memberSummaries.find((m) => m.id === memberId);
    return member ? `${member.firstName} ${member.surname}` : memberId;
  };
};
